import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess, type Paginated } from '../../lib/api';

interface AssociationDashboard {
  association: {
    id: string; name: string; type: string; slug: string; status: string;
    city: string | null; district: string | null; address: string | null;
    totalUnits: number; totalBuildings: number;
    bankName: string | null; bankIban: string | null;
    currentBalance: number | string; reserveFund: number | string;
    presidentId: string | null; vicePresidentId: string | null;
    secretaryId: string | null; treasurerId: string | null;
    company: { id: string; name: string };
    managers: Array<{ id: string; scope: string; manager: { user: { id: string; name: string; email: string | null; phone: string | null } } }>;
  };
  stats: { members: number; units: number; openTickets: number; pendingInvoices: number; pendingAmount: number };
  finance: {
    currentBalance: number; totalCollected: number; totalExpenses: number; reserveFund: number;
    avgMonthlyExpense: number; avgMonthlyCollection: number;
  };
  announcements: Array<{ id: string; title: string; priority: string; pinned: boolean; publishedAt: string }>;
  upcomingAssemblies: Array<{ id: string; title: string; scheduledAt: string }>;
  recentBills: Array<{ id: string; vendor: string; totalAmount: number | string; issueDate: string; status: string }>;
}

export function AdminAssociationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<'overview' | 'members' | 'finance' | 'tickets' | 'announcements' | 'compliance' | 'managers' | 'documents' | 'qr'>('overview');

  const { data, isLoading } = useQuery({
    queryKey: ['association-dashboard', id],
    queryFn: () => api.get<ApiSuccess<AssociationDashboard>>(`/api/associations/${id}/dashboard`),
    enabled: !!id,
  });

  if (isLoading || !data?.data) return <div className="text-gray-500 p-8">⟳ جاري التحميل…</div>;
  const d = data.data;
  const a = d.association;

  return (
    <div>
      <Link to="/admin/associations" className="text-sm text-navy hover:underline mb-2 inline-block">← العودة للجمعيات</Link>

      <Card className="mb-4">
        <CardBody>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-3xl font-black text-navy-dark">{a.name}</h1>
              <p className="text-sm text-gray-500">{a.address ?? `${a.city ?? ''} ${a.district ? `· ${a.district}` : ''}`}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Badge color="navy">{a.type}</Badge>
                <Badge color={a.status === 'ACTIVE' ? 'green' : 'amber'}>{a.status}</Badge>
                <Badge color="gold">شركة: {a.company.name}</Badge>
              </div>
            </div>
            <a href={`/associations/${a.slug}`} target="_blank" rel="noreferrer">
              <Button variant="outline">🔗 عرض الواجهة العامة</Button>
            </a>
          </div>
        </CardBody>
      </Card>

      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        <Tab active={tab === 'overview'}     onClick={() => setTab('overview')}>📊 الملخص</Tab>
        <Tab active={tab === 'members'}      onClick={() => setTab('members')}>👥 الأعضاء</Tab>
        <Tab active={tab === 'finance'}      onClick={() => setTab('finance')}>💰 المالية</Tab>
        <Tab active={tab === 'tickets'}      onClick={() => setTab('tickets')}>🛠️ الصيانة</Tab>
        <Tab active={tab === 'announcements'} onClick={() => setTab('announcements')}>📰 الأخبار</Tab>
        <Tab active={tab === 'compliance'}   onClick={() => setTab('compliance')}>📜 الامتثال</Tab>
        <Tab active={tab === 'managers'}     onClick={() => setTab('managers')}>👨‍💼 المديرون</Tab>
        <Tab active={tab === 'documents'}    onClick={() => setTab('documents')}>📁 الوثائق</Tab>
        <Tab active={tab === 'qr'}           onClick={() => setTab('qr')}>📱 QR للطباعة</Tab>
      </div>

      {tab === 'overview'      && <OverviewTab data={d} />}
      {tab === 'members'       && <MembersTab associationId={id!} />}
      {tab === 'finance'       && <FinanceTab associationId={id!} />}
      {tab === 'tickets'       && <TicketsTab associationId={id!} />}
      {tab === 'announcements' && <AnnouncementsTab associationId={id!} />}
      {tab === 'compliance'    && <ComplianceTab associationId={id!} />}
      {tab === 'managers'      && <ManagersTab associationId={id!} initial={a.managers} />}
      {tab === 'documents'     && <DocumentsTab associationId={id!} />}
      {tab === 'qr'            && <QrTab association={a} />}
    </div>
  );
}

// 📱 ━━━━━━━━━━━━━━ QR Code Tab ━━━━━━━━━━━━━━
function QrTab({ association }: { association: AssociationDashboard['association'] }) {
  const baseUrl = window.location.origin;
  const slug = association.slug;
  // 🔗 4 روابط مختلفة (يطبعهم ويلصقهم في أماكن مختلفة)
  const links = [
    { title: '🏠 صفحة الجمعية', subtitle: 'الوصول العام للأعضاء', url: `${baseUrl}/associations/${slug}`, color: '#1e3a5f' },
    { title: '🛠️ طلب صيانة',  subtitle: 'يفتح طلب صيانة مباشرة', url: `${baseUrl}/associations/${slug}?action=ticket`, color: '#dc2626' },
    { title: '🚨 رفع شكوى',    subtitle: 'تقديم شكوى للجمعية', url: `${baseUrl}/associations/${slug}?action=complaint`, color: '#d97706' },
    { title: '💰 رسومي',       subtitle: 'مراجعة الفواتير والدفع', url: `${baseUrl}/associations/${slug}?action=fees`, color: '#059669' },
  ];

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm">
        💡 <b>طريقة الاستخدام:</b> اطبع هذه الـ QR codes والصقها في مدخل المبنى، المصاعد، الممرات. الأعضاء يمسحوا الكود من جوالاتهم للوصول السريع.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {links.map((l, i) => <QrCard key={i} {...l} />)}
      </div>

      <div className="mt-4 flex justify-center">
        <Button variant="primary" onClick={() => window.print()}>🖨️ طباعة الكل</Button>
      </div>
    </div>
  );
}

function QrCard({ title, subtitle, url, color }: { title: string; subtitle: string; url: string; color: string }) {
  const [dataUrl, setDataUrl] = useState('');
  // ديناميكياً نولّد QR
  if (!dataUrl) {
    import('qrcode').then((QRCode) => {
      QRCode.toDataURL(url, { width: 320, margin: 2, color: { dark: color, light: '#FFFFFF' } })
        .then((u) => setDataUrl(u))
        .catch(() => {});
    }).catch(() => {});
  }
  return (
    <Card className="print:shadow-none print:border print:break-inside-avoid">
      <CardBody>
        <div className="text-center" style={{ borderTop: `4px solid ${color}` }}>
          <h3 className="font-black text-lg mt-3" style={{ color }}>{title}</h3>
          <p className="text-xs text-gray-500 mb-3">{subtitle}</p>
          {dataUrl ? (
            <img src={dataUrl} alt={title} className="mx-auto" style={{ maxWidth: 220 }} />
          ) : (
            <div className="bg-gray-100 mx-auto rounded animate-pulse" style={{ width: 220, height: 220 }} />
          )}
          <div className="text-xs text-gray-500 mt-2 font-mono break-all" dir="ltr">{url}</div>
          <div className="flex justify-center gap-2 mt-3 print:hidden">
            <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(url)}>📋 نسخ</Button>
            <a href={dataUrl} download={`qr-${title}.png`}><Button size="sm" variant="ghost">⬇️ تنزيل</Button></a>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Tabs ━━━━━━━━━━━━━━━━━━━━

function OverviewTab({ data }: { data: AssociationDashboard }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI title="الرصيد الحالي" value={`${data.finance.currentBalance.toLocaleString('en-US')} ر.س`} color="green" />
        <KPI title="المحصّل الإجمالي" value={`${data.finance.totalCollected.toLocaleString('en-US')} ر.س`} color="navy" />
        <KPI title="إجمالي المصروفات" value={`${data.finance.totalExpenses.toLocaleString('en-US')} ر.س`} color="amber" />
        <KPI title="احتياطي الصيانة" value={`${data.finance.reserveFund.toLocaleString('en-US')} ر.س`} color="gold" />
        <KPI title="الأعضاء" value={String(data.stats.members)} />
        <KPI title="الوحدات" value={`${data.stats.units}/${data.association.totalUnits}`} />
        <KPI title="طلبات صيانة مفتوحة" value={String(data.stats.openTickets)} color="red" />
        <KPI title="فواتير مستحقة" value={`${data.stats.pendingInvoices} (${data.stats.pendingAmount.toLocaleString('en-US')} ر.س)`} color="amber" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">📊 المتوسطات الشهرية</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>متوسط التحصيل/شهر:</span><b className="text-green-600">{data.finance.avgMonthlyCollection.toFixed(0)} ر.س</b></div>
              <div className="flex justify-between"><span>متوسط المصروفات/شهر:</span><b className="text-red-600">{data.finance.avgMonthlyExpense.toFixed(0)} ر.س</b></div>
              <div className="flex justify-between border-t pt-2"><span>التدفق الصافي:</span><b className={data.finance.avgMonthlyCollection - data.finance.avgMonthlyExpense >= 0 ? 'text-green-600' : 'text-red-600'}>{(data.finance.avgMonthlyCollection - data.finance.avgMonthlyExpense).toFixed(0)} ر.س</b></div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">📰 آخر الأخبار</h3>
            {data.announcements.length === 0 ? <div className="text-sm text-gray-500">لا توجد أخبار</div> : (
              <ul className="space-y-2 text-sm">
                {data.announcements.map((a) => (
                  <li key={a.id} className="flex items-center gap-2">
                    {a.pinned && <span className="text-gold">📌</span>}
                    {a.priority === 'URGENT' && <Badge color="red">عاجل</Badge>}
                    <span className="flex-1 truncate">{a.title}</span>
                    <span className="text-xs text-gray-500">{new Date(a.publishedAt).toLocaleDateString('ar-SA')}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">🗳️ جمعيات عمومية قادمة</h3>
            {data.upcomingAssemblies.length === 0 ? <div className="text-sm text-gray-500">لا توجد جمعيات مجدولة</div> : (
              <ul className="space-y-2 text-sm">
                {data.upcomingAssemblies.map((a) => (
                  <li key={a.id} className="flex justify-between"><span>{a.title}</span><b>{new Date(a.scheduledAt).toLocaleDateString('ar-SA')}</b></li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">🧾 آخر الفواتير</h3>
            {data.recentBills.length === 0 ? <div className="text-sm text-gray-500">لا توجد فواتير</div> : (
              <ul className="space-y-2 text-sm">
                {data.recentBills.map((b) => (
                  <li key={b.id} className="flex justify-between">
                    <span>{b.vendor}</span>
                    <b>{Number(b.totalAmount).toLocaleString('en-US')} ر.س</b>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function KPI({ title, value, color = 'navy' }: { title: string; value: string; color?: 'green' | 'navy' | 'amber' | 'red' | 'gold' }) {
  const colors: Record<string, string> = {
    green: 'text-green-600', navy: 'text-navy-dark', amber: 'text-amber-600', red: 'text-red-600', gold: 'text-gold',
  };
  return (
    <Card><CardBody>
      <div className="text-xs text-gray-500 mb-1">{title}</div>
      <div className={`text-xl font-black ${colors[color]}`}>{value}</div>
    </CardBody></Card>
  );
}

function MembersTab({ associationId }: { associationId: string }) {
  const qc = useQueryClient();
  const [openAdd, setOpenAdd] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [shownLink, setShownLink] = useState<string | null>(null);

  const exportXlsx = async () => {
    const res = await fetch(`/api/associations/${associationId}/members/export`, { credentials: 'include' });
    if (!res.ok) { alert('فشل التصدير'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `members_${associationId}.xlsx`;
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(url);
  };

  const { data } = useQuery({
    queryKey: ['association', associationId, 'members'],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; fullName: string; phone: string | null; email: string | null; role: string; committeeRole: string; activatedAt: string | null; unit: { unitNumber: string } | null; user: { id: string; name: string; image: string | null } | null }>>>(`/api/associations/${associationId}/members`),
  });
  const deleteM = useMutation({
    mutationFn: (memberId: string) => api.delete(`/api/associations/${associationId}/members/${memberId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['association', associationId, 'members'] }),
  });
  const resendM = useMutation({
    mutationFn: (memberId: string) => api.post<ApiSuccess<{ link: string }>>(`/api/associations/${associationId}/members/${memberId}/resend-invite`, {}),
    onSuccess: (r) => setShownLink(r.data.link),
  });

  return (
    <>
      <div className="flex justify-end gap-2 mb-3 flex-wrap">
        <Button variant="ghost" onClick={exportXlsx}>📥 تصدير Excel</Button>
        <Button variant="ghost" onClick={() => setOpenImport(true)}>📤 رفع ملف Excel</Button>
        <Button variant="primary" onClick={() => setOpenAdd(true)}>+ إضافة عضو</Button>
      </div>
      {!data?.data?.length ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">لا يوجد أعضاء</div></CardBody></Card>
      ) : (
        <Card><CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-right px-3 py-2">العضو</th><th className="text-right px-3 py-2">الوحدة</th><th className="text-right px-3 py-2">الدور</th><th className="text-right px-3 py-2">اللجنة</th><th className="text-right px-3 py-2">الحالة</th><th></th>
            </tr></thead>
            <tbody>
              {data.data.map((m) => (
                <tr key={m.id} className="border-b">
                  <td className="px-3 py-2"><div className="font-bold">{m.fullName}</div><div className="text-xs text-gray-500" dir="ltr">{m.phone ?? m.email ?? '—'}</div></td>
                  <td className="px-3 py-2">{m.unit?.unitNumber ?? '—'}</td>
                  <td className="px-3 py-2"><Badge color="navy">{m.role}</Badge></td>
                  <td className="px-3 py-2">{m.committeeRole !== 'NONE' && <Badge color="gold">{m.committeeRole}</Badge>}</td>
                  <td className="px-3 py-2">{m.activatedAt ? <Badge color="green">مفعّل</Badge> : <Badge color="amber">بانتظار التفعيل</Badge>}</td>
                  <td className="px-3 py-2">
                    {!m.activatedAt && <button onClick={() => resendM.mutate(m.id)} className="text-xs text-navy hover:underline mr-2">إعادة إرسال</button>}
                    <button onClick={() => { if (confirm('حذف العضو؟')) deleteM.mutate(m.id); }} className="text-red-600 hover:text-red-800">🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody></Card>
      )}
      {openAdd && <AddMemberModal associationId={associationId} onClose={() => setOpenAdd(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['association', associationId, 'members'] })} />}
      {openImport && <ImportExcelModal associationId={associationId} onClose={() => setOpenImport(false)} onDone={() => qc.invalidateQueries({ queryKey: ['association', associationId, 'members'] })} />}
      {shownLink && <InviteLinkModal link={shownLink} onClose={() => setShownLink(null)} />}
    </>
  );
}

function AddedMemberLinkBox({ link, onClose }: { link: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full"><CardBody>
        <h3 className="font-black text-navy-dark mb-2">✓ تمت إضافة العضو</h3>
        <p className="text-sm text-gray-600 mb-2">أرسلنا الدعوة بالإيميل/SMS. الرابط:</p>
        <div className="bg-gray-100 rounded-lg p-2 text-xs font-mono break-all mb-3" dir="ltr">{link}</div>
        <div className="flex gap-2">
          <Button variant="primary" className="flex-1" onClick={copy}>{copied ? '✓ تم النسخ' : '📋 نسخ الرابط'}</Button>
          <Button variant="ghost" onClick={onClose}>تم</Button>
        </div>
      </CardBody></Card>
    </div>
  );
}

function InviteLinkModal({ link, onClose }: { link: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-black text-navy-dark">📨 رابط الدعوة</h3>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">أرسلنا الرابط للعضو بالإيميل/SMS. تقدر أيضاً تنسخه يدوياً:</p>
          <div className="bg-gray-100 rounded-lg p-3 text-xs font-mono break-all" dir="ltr">{link}</div>
          <div className="flex gap-2">
            <Button variant="primary" className="flex-1" onClick={copy}>{copied ? '✓ تم النسخ' : '📋 نسخ الرابط'}</Button>
            <Button variant="ghost" onClick={onClose}>إغلاق</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImportExcelModal({ associationId, onClose, onDone }: { associationId: string; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{ total: number; created: number; skipped: number; errors: Array<{ row: number; reason: string }> } | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`/api/associations/${associationId}/members/import`, { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      setResult(json.data);
      onDone();
    } catch (e) {
      alert((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">📤 رفع ملف Excel</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          {!result ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
                💡 <b>نصيحة:</b> صدّر القالب أولاً من زر "📥 تصدير Excel" — يحتوي على قوائم منسدلة جاهزة للاختيار.
              </div>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm border-2 border-dashed border-gray-300 rounded-xl p-4 cursor-pointer"
              />
              {file && <div className="text-sm text-gray-600">📎 {file.name}</div>}
            </>
          ) : (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="font-black text-green-800 mb-2">✅ تمت العملية</div>
                <div className="text-sm">
                  <div>إجمالي الصفوف: <b>{result.total}</b></div>
                  <div>تمت الإضافة: <b className="text-green-700">{result.created}</b></div>
                  <div>تم التخطي: <b className="text-amber-700">{result.skipped}</b></div>
                </div>
              </div>
              {result.errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 max-h-48 overflow-y-auto">
                  <div className="font-bold text-amber-800 mb-2">⚠️ تحذيرات:</div>
                  <ul className="text-xs space-y-1">
                    {result.errors.map((e, i) => <li key={i}>صف {e.row}: {e.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          {!result ? (
            <>
              <Button variant="ghost" onClick={onClose}>إلغاء</Button>
              <Button variant="primary" onClick={upload} loading={busy} disabled={!file}>📤 رفع</Button>
            </>
          ) : (
            <Button variant="primary" onClick={onClose}>تم</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddMemberModal({ associationId, onClose, onSaved }: { associationId: string; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ fullName: '', email: '', phone: '', nationalId: '', role: 'OWNER', sendInvite: true });
  const [err, setErr] = useState('');
  const [link, setLink] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ inviteLink: string | null }>>(`/api/associations/${associationId}/members`, f),
    onSuccess: (r) => { onSaved(); if (r.data.inviteLink) setLink(r.data.inviteLink); else onClose(); },
    onError: (e: Error) => setErr(e.message),
  });
  if (link) return <AddedMemberLinkBox link={link} onClose={onClose} />;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">+ إضافة عضو</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="الاسم الكامل *" value={f.fullName} onChange={(e) => setF({ ...f, fullName: e.target.value })} />
          <Input label="رقم الهوية" value={f.nationalId} onChange={(e) => setF({ ...f, nationalId: e.target.value })} dir="ltr" />
          <Input label="الإيميل" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} dir="ltr" />
          <Input label="الجوال" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} dir="ltr" placeholder="+9665..." />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">نوع العضوية</label>
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="OWNER">مالك</option>
              <option value="CO_OWNER">مالك مشترك</option>
              <option value="TENANT">مستأجر</option>
              <option value="PROXY">وكيل قانوني</option>
              <option value="RESIDENT">مقيم</option>
            </select>
          </div>
          <label className="flex items-center gap-2 mt-7"><input type="checkbox" checked={f.sendInvite} onChange={(e) => setF({ ...f, sendInvite: e.target.checked })} className="w-5 h-5 accent-navy" /><span className="text-sm font-bold">إرسال رابط دعوة</span></label>
        </div>
        {err && <div className="mx-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.fullName}>إضافة</Button>
        </div>
      </div>
    </div>
  );
}

function FinanceTab({ associationId }: { associationId: string }) {
  const { data: summary } = useQuery({
    queryKey: ['association', associationId, 'finance-summary'],
    queryFn: () => api.get<ApiSuccess<{
      invoices: { paidCount: number; paidAmount: number; openCount: number; openAmount: number; overdueCount: number; overdueAmount: number };
      bills: { paidCount: number; paidAmount: number; avgMonthly: number };
      collectionRate: number;
      netCashFlow: number;
      billsByCategory: Array<{ category: string; count: number; total: number }>;
    }>>(`/api/associations/${associationId}/finance/summary`),
  });
  const { data: bills } = useQuery({
    queryKey: ['association', associationId, 'bills'],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; vendor: string; category: string; totalAmount: number | string; issueDate: string; dueDate: string; status: string }>>>(`/api/associations/${associationId}/bills`),
  });

  const s = summary?.data;
  return (
    <div className="space-y-4">
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI title="نسبة التحصيل" value={`${s.collectionRate.toFixed(1)}%`} color={s.collectionRate >= 80 ? 'green' : 'amber'} />
          <KPI title="التدفق الصافي" value={`${s.netCashFlow.toLocaleString('en-US')} ر.س`} color={s.netCashFlow >= 0 ? 'green' : 'red'} />
          <KPI title="فواتير متأخرة" value={`${s.invoices.overdueCount} (${s.invoices.overdueAmount.toLocaleString('en-US')})`} color="red" />
          <KPI title="متوسط الفواتير/شهر" value={`${s.bills.avgMonthly.toFixed(0)} ر.س`} />
        </div>
      )}

      <Card>
        <CardBody>
          <h3 className="font-black text-navy-dark mb-3">🧾 آخر فواتير الموردين</h3>
          {!bills?.data?.length ? <div className="text-sm text-gray-500">لا توجد فواتير</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="text-right px-3 py-2">المورد</th><th className="text-right px-3 py-2">الفئة</th><th className="text-right px-3 py-2">المبلغ</th><th className="text-right px-3 py-2">الاستحقاق</th><th className="text-right px-3 py-2">الحالة</th>
                </tr></thead>
                <tbody>
                  {bills.data.slice(0, 20).map((b) => (
                    <tr key={b.id} className="border-b">
                      <td className="px-3 py-2 font-bold">{b.vendor}</td>
                      <td className="px-3 py-2 text-xs">{b.category}</td>
                      <td className="px-3 py-2 font-bold">{Number(b.totalAmount).toLocaleString('en-US')} ر.س</td>
                      <td className="px-3 py-2 text-xs">{new Date(b.dueDate).toLocaleDateString('ar-SA')}</td>
                      <td className="px-3 py-2"><Badge color={b.status === 'PAID' ? 'green' : 'amber'}>{b.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function TicketsTab({ associationId }: { associationId: string }) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['association', associationId, 'tickets', statusFilter],
    queryFn: () => api.get<Paginated<Ticket>>(`/api/associations/${associationId}/tickets`, { query: { status: statusFilter, limit: 100 } }),
  });
  const items = data?.data ?? [];

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm"
          >
            <option value="">كل الحالات</option>
            <option value="NEW">🆕 جديد</option>
            <option value="ASSIGNED">📋 مسند</option>
            <option value="TECHNICIAN_ENROUTE">🚗 التقني في الطريق</option>
            <option value="IN_PROGRESS">⚙️ قيد العمل</option>
            <option value="AWAITING_PARTS">⏳ بانتظار قطع</option>
            <option value="AWAITING_APPROVAL">📝 بانتظار موافقة</option>
            <option value="COMPLETED">✅ منجز</option>
            <option value="CANCELLED">❌ ملغى</option>
          </select>
        </CardBody>
      </Card>

      {isLoading ? (
        <div className="text-gray-500 text-center py-8">⟳ جاري التحميل…</div>
      ) : !items.length ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">
          <div className="text-5xl mb-3 opacity-50">🛠️</div>
          <div>لا توجد طلبات صيانة</div>
        </div></CardBody></Card>
      ) : (
        <Card><CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-right px-3 py-2">الرقم</th>
              <th className="text-right px-3 py-2">العنوان</th>
              <th className="text-right px-3 py-2">الفئة</th>
              <th className="text-right px-3 py-2">الأولوية</th>
              <th className="text-right px-3 py-2">التقني</th>
              <th className="text-right px-3 py-2">الحالة</th>
              <th></th>
            </tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setOpenId(t.id)}>
                  <td className="px-3 py-2 font-mono text-xs" dir="ltr">{t.ticketNumber}</td>
                  <td className="px-3 py-2"><div className="font-bold">{t.title}</div>{t.location && <div className="text-xs text-gray-500">{t.location}</div>}</td>
                  <td className="px-3 py-2 text-xs">{t.category}</td>
                  <td className="px-3 py-2"><Badge color={t.urgency === 'EMERGENCY' ? 'red' : t.urgency === 'HIGH' ? 'amber' : 'navy'}>{t.urgency}</Badge></td>
                  <td className="px-3 py-2 text-xs">{t.assignedTechnician ?? '—'}</td>
                  <td className="px-3 py-2"><Badge color={t.status === 'COMPLETED' ? 'green' : t.status === 'CANCELLED' ? 'red' : 'amber'}>{t.status}</Badge></td>
                  <td className="px-3 py-2"><Button size="sm" variant="primary">إدارة</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody></Card>
      )}

      {openId && items.find((t) => t.id === openId) && (
        <TicketManageModal
          associationId={associationId}
          ticket={items.find((t) => t.id === openId)!}
          onClose={() => setOpenId(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['association', associationId, 'tickets'] })}
        />
      )}
    </>
  );
}

interface Ticket {
  id: string; ticketNumber: string; title: string; description: string;
  category: string; urgency: string; status: string;
  location: string | null; createdAt: string;
  assignedTechnician: string | null; technicianPhone: string | null;
  estimatedCost: number | string | null; actualCost: number | string | null;
  resolution: string | null;
}

function TicketManageModal({ associationId, ticket, onClose, onSaved }: {
  associationId: string; ticket: Ticket; onClose: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState({
    status: ticket.status,
    urgency: ticket.urgency,
    assignedTechnician: ticket.assignedTechnician ?? '',
    technicianPhone: ticket.technicianPhone ?? '',
    estimatedCost: ticket.estimatedCost?.toString() ?? '',
    actualCost: ticket.actualCost?.toString() ?? '',
    resolution: ticket.resolution ?? '',
    note: '',
  });
  const m = useMutation({
    mutationFn: () => api.put(`/api/associations/${associationId}/tickets/${ticket.id}`, {
      status: f.status,
      urgency: f.urgency,
      assignedTechnician: f.assignedTechnician || null,
      technicianPhone: f.technicianPhone || null,
      estimatedCost: f.estimatedCost ? Number(f.estimatedCost) : null,
      actualCost: f.actualCost ? Number(f.actualCost) : null,
      resolution: f.resolution || null,
      note: f.note || undefined,
    }),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black text-navy-dark">{ticket.title}</h2>
            <div className="text-xs text-gray-500 font-mono" dir="ltr">{ticket.ticketNumber}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-gray-50 rounded-xl p-3 text-sm">
            <div className="text-xs text-gray-500 mb-1">الوصف</div>
            <div className="whitespace-pre-line">{ticket.description}</div>
            {ticket.location && <div className="text-xs text-gray-500 mt-2">📍 {ticket.location}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الحالة</label>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
                <option value="NEW">🆕 جديد</option>
                <option value="ASSIGNED">📋 مسند</option>
                <option value="TECHNICIAN_ENROUTE">🚗 التقني في الطريق</option>
                <option value="IN_PROGRESS">⚙️ قيد العمل</option>
                <option value="AWAITING_PARTS">⏳ بانتظار قطع</option>
                <option value="AWAITING_APPROVAL">📝 بانتظار موافقة</option>
                <option value="COMPLETED">✅ منجز</option>
                <option value="CANCELLED">❌ ملغى</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الأولوية</label>
              <select value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
                <option value="LOW">منخفضة</option>
                <option value="NORMAL">عادية</option>
                <option value="HIGH">مرتفعة</option>
                <option value="EMERGENCY">طوارئ</option>
              </select>
            </div>
            <Input label="اسم التقني" value={f.assignedTechnician} onChange={(e) => setF({ ...f, assignedTechnician: e.target.value })} />
            <Input label="جوال التقني" value={f.technicianPhone} onChange={(e) => setF({ ...f, technicianPhone: e.target.value })} dir="ltr" />
            <Input label="التكلفة المتوقعة" type="number" value={f.estimatedCost} onChange={(e) => setF({ ...f, estimatedCost: e.target.value })} />
            <Input label="التكلفة الفعلية" type="number" value={f.actualCost} onChange={(e) => setF({ ...f, actualCost: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الحل / الإنجاز</label>
            <textarea value={f.resolution} onChange={(e) => setF({ ...f, resolution: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">ملاحظة (للسجل الزمني)</label>
            <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="مثلاً: تم الاتصال بالتقني" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}

function AnnouncementsTab({ associationId }: { associationId: string }) {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const { data } = useQuery({
    queryKey: ['association', associationId, 'announcements'],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; title: string; content: string; priority: string; pinned: boolean; publishedAt: string }>>>(`/api/associations/${associationId}/announcements`),
  });
  const deleteM = useMutation({
    mutationFn: (annId: string) => api.delete(`/api/associations/${associationId}/announcements/${annId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['association', associationId, 'announcements'] }),
  });
  return (
    <>
      <div className="flex justify-end mb-3"><Button variant="primary" onClick={() => setOpenCreate(true)}>+ إعلان جديد</Button></div>
      {!data?.data?.length ? <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد إعلانات</div></CardBody></Card> : (
        <div className="space-y-3">
          {data.data.map((a) => (
            <Card key={a.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {a.pinned && <span className="text-gold">📌</span>}
                      <Badge color={a.priority === 'URGENT' ? 'red' : a.priority === 'IMPORTANT' ? 'amber' : 'navy'}>{a.priority}</Badge>
                      <span className="text-xs text-gray-500">{new Date(a.publishedAt).toLocaleDateString('ar-SA')}</span>
                    </div>
                    <h3 className="font-black text-navy-dark mb-1">{a.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 whitespace-pre-line">{a.content}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف الإعلان؟')) deleteM.mutate(a.id); }}>🗑</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {openCreate && <CreateAnnouncementModal associationId={associationId} onClose={() => setOpenCreate(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['association', associationId, 'announcements'] })} />}
    </>
  );
}

function CreateAnnouncementModal({ associationId, onClose, onSaved }: { associationId: string; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ title: '', content: '', priority: 'NORMAL', pinned: false });
  const m = useMutation({
    mutationFn: () => api.post(`/api/associations/${associationId}/announcements`, f),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">📰 إعلان جديد</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <Input label="العنوان *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">المحتوى *</label>
            <textarea value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} rows={6} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الأولوية</label>
              <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
                <option value="NORMAL">عادية</option>
                <option value="IMPORTANT">مهمة</option>
                <option value="URGENT">عاجلة</option>
              </select>
            </div>
            <label className="flex items-center gap-2 mt-7"><input type="checkbox" checked={f.pinned} onChange={(e) => setF({ ...f, pinned: e.target.checked })} className="w-5 h-5 accent-gold" /><span className="text-sm font-bold">📌 تثبيت</span></label>
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.title || !f.content}>نشر</Button>
        </div>
      </div>
    </div>
  );
}

function ComplianceTab({ associationId }: { associationId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['association', associationId, 'compliance'],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; title: string; description: string; legalReference: string | null; category: string; priority: string; status: string; dueDate: string; frequency: string }>>>(`/api/associations/${associationId}/compliance`),
  });
  const seedM = useMutation({
    mutationFn: () => api.post(`/api/associations/${associationId}/compliance/seed`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['association', associationId, 'compliance'] }),
  });
  const completeM = useMutation({
    mutationFn: (taskId: string) => api.put(`/api/associations/${associationId}/compliance/${taskId}/complete`, { notes: 'تم الإنجاز' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['association', associationId, 'compliance'] }),
  });

  const tasks = data?.data ?? [];
  return (
    <>
      {tasks.length === 0 && (
        <Card className="mb-3"><CardBody>
          <div className="text-center py-8">
            <div className="text-5xl mb-3">📜</div>
            <p className="text-gray-600 mb-4">لم يتم توليد مهام الامتثال بعد</p>
            <Button variant="primary" onClick={() => seedM.mutate()} loading={seedM.isPending}>🌱 توليد مهام الامتثال الافتراضية (12 مهمة)</Button>
            <p className="text-xs text-gray-500 mt-3">حسب نظام ملكية الوحدات العقارية وفرزها وإدارتها</p>
          </div>
        </CardBody></Card>
      )}
      {tasks.length > 0 && (
        <div className="space-y-3">
          {tasks.map((t) => (
            <Card key={t.id} className={t.status === 'OVERDUE' ? 'border-2 border-red-300' : ''}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge color={t.priority === 'CRITICAL' ? 'red' : t.priority === 'HIGH' ? 'amber' : 'navy'}>{t.priority}</Badge>
                      <Badge color={t.status === 'COMPLETED' ? 'green' : t.status === 'OVERDUE' ? 'red' : 'amber'}>{t.status}</Badge>
                      <span className="text-xs text-gray-500">يستحق: {new Date(t.dueDate).toLocaleDateString('ar-SA')}</span>
                    </div>
                    <h4 className="font-black text-navy-dark">{t.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{t.description}</p>
                    {t.legalReference && <p className="text-xs text-gray-500 mt-1">📚 {t.legalReference}</p>}
                  </div>
                  {t.status !== 'COMPLETED' && (
                    <Button size="sm" variant="primary" onClick={() => completeM.mutate(t.id)}>✓ تأكيد الإنجاز</Button>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function ManagersTab({ associationId, initial }: { associationId: string; initial: AssociationDashboard['association']['managers'] }) {
  const qc = useQueryClient();
  const [openAdd, setOpenAdd] = useState(false);
  const removeM = useMutation({
    mutationFn: (assignmentId: string) => api.delete(`/api/associations/${associationId}/managers/${assignmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['association-dashboard', associationId] }),
  });

  return (
    <>
      <div className="flex justify-end mb-3"><Button variant="primary" onClick={() => setOpenAdd(true)}>+ إسناد مدير</Button></div>
      <div className="space-y-3">
        {initial.length === 0 && <Card><CardBody><div className="text-center py-8 text-gray-500">لا يوجد مديرون مسندون</div></CardBody></Card>}
        {initial.map((m) => (
          <Card key={m.id}>
            <CardBody>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-navy-dark text-white flex items-center justify-center text-2xl">👨‍💼</div>
                  <div>
                    <div className="font-black">{m.manager.user.name}</div>
                    <div className="text-xs text-gray-500" dir="ltr">{m.manager.user.email ?? m.manager.user.phone}</div>
                    <Badge color={m.scope === 'PRIMARY' ? 'gold' : 'navy'}>{m.scope}</Badge>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { if (confirm('إنهاء التعيين؟')) removeM.mutate(m.id); }}>إنهاء</Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
      {openAdd && <AssignManagerModal associationId={associationId} onClose={() => setOpenAdd(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['association-dashboard', associationId] })} />}
    </>
  );
}

function AssignManagerModal({ associationId, onClose, onSaved }: { associationId: string; onClose: () => void; onSaved: () => void }) {
  const [managerId, setManagerId] = useState('');
  const [scope, setScope] = useState('PRIMARY');
  const [err, setErr] = useState('');
  const { data } = useQuery({
    queryKey: ['property-managers'],
    queryFn: () => api.get<ApiSuccess<Array<{ userId: string; user: { name: string; email: string | null }; company: { name: string } }>>>('/api/admin/management-companies/managers/list'),
  });
  const m = useMutation({
    mutationFn: () => api.post(`/api/associations/${associationId}/managers`, { managerId, scope }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">+ إسناد مدير</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-bold text-ink mb-1">المدير *</label>
            <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="">— اختر —</option>
              {data?.data?.map((m) => <option key={m.userId} value={m.userId}>{m.user.name} ({m.company.name})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">النطاق</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="PRIMARY">رئيسي</option>
              <option value="ASSISTANT">مساعد</option>
              <option value="TEMPORARY">مؤقت</option>
            </select>
          </div>
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!managerId}>إسناد</Button>
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ associationId }: { associationId: string }) {
  const { data } = useQuery({
    queryKey: ['association', associationId, 'documents'],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; title: string; category: string; fileUrl: string; createdAt: string }>>>(`/api/associations/${associationId}/documents`),
  });
  const items = data?.data ?? [];
  if (!items.length) return <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد وثائق</div></CardBody></Card>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((d) => (
        <Card key={d.id}><CardBody>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold">{d.title}</div>
              <Badge color="navy">{d.category}</Badge>
            </div>
            <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-navy hover:underline">عرض</a>
          </div>
        </CardBody></Card>
      ))}
    </div>
  );
}

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px whitespace-nowrap ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}
